import { ApiError } from "@carepass/api-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { downloadAdminFile, uploadAdminFile } from "../../app/files";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { useAdminApplication, useAdminSession, useAdminUiStore } from "../../app/providers";
import { parseAdminEntityId, pathForAdminEntityScreen } from "../../app/routes";
import { failure } from "../workspace/behavior";
import type { Schemas } from "../workspace/api.generated";
import type { Command } from "../workspace/model";
import { StateNotice } from "../workspace/shared";
import { BenefitView } from "./components/BenefitView";
import type { BenefitDocumentPreview } from "./components/BenefitView";
import { useBenefitDetail, useBenefitMutation } from "./queries";

function queryState(query: ReturnType<typeof useBenefitDetail>) {
  if (query.isPending) return "loading" as const;
  if (query.error instanceof ApiError && query.error.kind === "forbidden") return "denied" as const;
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found" as const;
  if (query.isError) return "error" as const;
  return "ready" as const;
}

function unresolvedBenefitId(
  commands: Readonly<Record<string, { readonly startedAt: string }>>,
): number | undefined {
  const entry = Object.entries(commands)
    .filter(([key]) => key.startsWith("benefit-command:"))
    .sort(([, left], [, right]) => right.startedAt.localeCompare(left.startedAt))[0];
  const parsed = entry ? Number(entry[0].slice("benefit-command:".length)) : Number.NaN;
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function BenefitDetailPageForClaim({ claimId }: { readonly claimId: number }) {
  const services = useAdminApplication();
  const session = useAdminSession();
  const commands = useAdminUiStore((state) => state.unresolvedCommands);
  const markCommandUnresolved = useAdminUiStore((state) => state.markCommandUnresolved);
  const resolveCommand = useAdminUiStore((state) => state.resolveCommand);
  const unresolvedClaimId = useMemo(() => unresolvedBenefitId(commands), [commands]);
  const detail = useBenefitDetail(claimId);
  const mutation = useBenefitMutation();
  const navigation = useAdminNavigation({
    contextKey: "benefits",
    defaultListPath: "/benefits?page=0&size=20",
    originLabel: "혜택지원 상세",
  });
  const [notice, setNotice] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  const fileRequests = useRef(new Set<AbortController>());
  const authorities = session.authSession?.authorities ?? [];

  useEffect(() => () => {
    for (const controller of fileRequests.current) controller.abort();
    fileRequests.current.clear();
  }, []);

  const refresh = async () => {
    setRecoveryError("");
    const result = await detail.refetch();
    if (result.error) throw result.error;
    if (unresolvedClaimId === claimId) resolveCommand(`benefit-command:${claimId}`);
    return result.data;
  };

  const runCommand = async (command: Command) => {
    const commandKey = `benefit-command:${claimId}`;
    markCommandUnresolved(commandKey, {
      operation: command.operation,
      targetId: String(claimId),
      startedAt: new Date().toISOString(),
    });
    try {
      switch (command.operation) {
        case "POST /api/v1/admin/benefits/{claimId}/approval-requests":
          return await mutation.mutateAsync({ kind: "request", claimId, body: command.body });
        case "POST /api/v1/admin/benefits/{claimId}/reject":
          return await mutation.mutateAsync({ kind: "claim-reject", claimId, body: command.body });
        case "POST /api/v1/admin/benefits/approval-requests/{approvalId}/decision":
          return await mutation.mutateAsync({
            kind: "decision",
            claimId,
            approvalId: Number(command.params?.approvalId),
            body: command.body,
          });
        case "POST /api/v1/admin/benefits/{claimId}/manual-payout":
          return await mutation.mutateAsync({ kind: "payout", claimId, body: command.body });
        default:
          resolveCommand(commandKey);
          throw new Error(`Unsupported benefit command: ${command.operation}`);
      }
    } catch (cause) {
      if (!failure(cause).reconcile) resolveCommand(commandKey);
      throw cause;
    }
  };

  const commandComplete = async (updated: Schemas["BenefitClaimView"]) => {
    resolveCommand(`benefit-command:${claimId}`);
    setNotice(`${updated.claimNo} 처리 결과가 ${updated.status} 상태로 반영되었습니다.`);
    const result = await detail.refetch();
    if (result.error) {
      setRecoveryError("명령 결과는 확인했지만 신청 상세를 갱신하지 못했습니다. 최신 상태를 다시 조회해 주세요.");
      throw result.error;
    }
  };

  const uploadEvidence = async (file: File) => {
    const request = new AbortController();
    fileRequests.current.add(request);
    try {
      return await uploadAdminFile(services, file, "PAYOUT_EVIDENCE", request.signal);
    } finally {
      fileRequests.current.delete(request);
    }
  };

  const downloadDocument = async (
    document: Schemas["BenefitDocumentAdminView"],
  ): Promise<BenefitDocumentPreview> => {
    const request = new AbortController();
    fileRequests.current.add(request);
    try {
      const blob = await downloadAdminFile(services, document.fileAssetId, {
        businessRefType: "BENEFIT",
        businessRefId: claimId,
      }, request.signal);
      return {
        url: services.sessionLifecycle.createPreviewUrl(blob),
        contentType: blob.type || "application/octet-stream",
      };
    } finally {
      fileRequests.current.delete(request);
    }
  };

  const releasePreview = useCallback((url: string) => {
    services.sessionLifecycle.revokePreviewUrl(url);
  }, [services.sessionLifecycle]);

  const memberId = detail.data?.claim.memberId;
  const memberHref = memberId && authorities.includes("MEMBER_READ")
    ? pathForAdminEntityScreen("06", memberId)
    : undefined;

  return (
    <BenefitView
      mode="detail"
      filters={{ status: "", memberId: "", from: "", to: "" }}
      listState="ready"
      detail={detail.data}
      detailState={queryState(detail)}
      selectedId={claimId}
      actorId={session.actor?.id ?? 0}
      canRequest={authorities.includes("BENEFIT_REQUEST")}
      canApprove={authorities.includes("BENEFIT_APPROVE")}
      unresolvedClaimId={unresolvedClaimId === claimId ? unresolvedClaimId : undefined}
      notice={notice}
      recoveryError={recoveryError}
      backHref={navigation.backPath}
      backLabel={navigation.backLabel}
      onBack={navigation.backToList}
      detailHref={(claim) => pathForAdminEntityScreen("24", claim.claimId)}
      memberHref={memberHref}
      onOpenMember={memberHref ? () => navigation.openRelated(memberHref, "혜택지원 상세") : undefined}
      onSearch={() => undefined}
      onPageChange={() => undefined}
      onSelect={() => undefined}
      onRetryList={() => undefined}
      onRetryDetail={() => { void detail.refetch(); }}
      onRefresh={refresh}
      onCommand={runCommand}
      onCommandComplete={commandComplete}
      onUploadPayoutEvidence={uploadEvidence}
      onDownloadDocument={downloadDocument}
      onReleasePreview={releasePreview}
    />
  );
}

export function BenefitDetailPage() {
  const claimId = parseAdminEntityId(useParams().claimId);
  if (claimId === null) return <StateNotice state="not-found" />;
  return <BenefitDetailPageForClaim key={claimId} claimId={claimId} />;
}
